import { useGame } from '../context/GameContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import { ShareIcon } from '../components/icons';
import { AppScreen } from '../components/AppScreen';

const MAX_PLAYERS = 5;

export function LobbyPage() {
  const { game, gameCode, myPlayerId, startGame, leaveGame } = useGame();
  const { isDark, toggle } = useTheme();

  if (!game || !gameCode) return null;

  const isHost = game.hostId === myPlayerId;
  const joinUrl = `${window.location.origin}/join/${gameCode}`;
  const openSeats = Math.max(0, MAX_PLAYERS - game.players.length);

  const handleShare = async () => {
    const shareData = {
      title: 'Join my Plate Seeker game!',
      text: `Join my road trip license plate game. Code: ${gameCode}`,
      url: joinUrl,
    };
    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch {
        /* cancelled */
      }
    } else {
      await navigator.clipboard.writeText(joinUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <AppScreen>
      {/* Nav bar */}
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex items-center justify-between pt-[calc(env(safe-area-inset-top)+14px)] pb-2 px-4">
        <button
          onClick={leaveGame}
          aria-label="Leave lobby"
          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-atlas-ink dark:text-atlas-ink-dark"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="font-serif text-[17px] font-semibold text-atlas-ink dark:text-atlas-ink-dark">Game Lobby</span>
        <ThemeToggle isDark={isDark} onToggle={toggle} />
      </div>

      <main className="flex-1 px-6 pt-2 flex flex-col gap-5">
        {/* Game code placard */}
        <div className="border-[1.5px] border-dashed border-atlas-brass dark:border-atlas-brass-dark rounded-2xl p-[18px] flex flex-col items-center gap-2 bg-atlas-surface dark:bg-atlas-surface-dark">
          <span className="text-[11px] font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Share This Code
          </span>
          <span className="font-mono text-[32px] font-semibold tracking-wide text-atlas-ink dark:text-atlas-ink-dark">
            {gameCode}
          </span>
          <button
            onClick={handleShare}
            className="mt-1 h-11 px-5 rounded-full border-[1.5px] border-atlas-brass dark:border-atlas-brass-dark text-atlas-brass dark:text-atlas-brass-dark font-semibold text-sm flex items-center justify-center gap-2 hover:bg-atlas-brass/10 dark:hover:bg-atlas-brass-dark/15 transition-colors"
          >
            <ShareIcon className="w-4 h-4" />
            Share Invite Link
          </button>
        </div>

        {/* Player list */}
        <div className="flex flex-col gap-2.5">
          <span className="text-xs font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Players ({game.players.length}/{MAX_PLAYERS})
          </span>

          {game.players.map(p => (
            <div key={p.id} className="flex items-center gap-3 bg-atlas-surface dark:bg-atlas-surface-dark rounded-xl px-3.5 py-3">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                  p.id === game.hostId
                    ? 'bg-atlas-ink dark:bg-atlas-ink-dark text-atlas-bg dark:text-atlas-bg-dark'
                    : 'bg-atlas-avatar dark:bg-atlas-avatar-dark text-atlas-bg dark:text-atlas-ink-dark'
                }`}
              >
                {p.name.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-atlas-ink dark:text-atlas-ink-dark truncate">
                  {p.name}
                  {p.id === myPlayerId && (
                    <span className="ml-1 text-xs font-normal text-atlas-ink-soft dark:text-atlas-ink-soft-dark">(you)</span>
                  )}
                </div>
                {p.currentState && (
                  <div
                    className={`text-xs flex items-center gap-1 ${
                      p.isGPS ? 'text-atlas-pine dark:text-atlas-pine-dark' : 'text-atlas-brick dark:text-atlas-brick-dark'
                    }`}
                  >
                    <svg viewBox="0 0 8 8" width="8" height="8" fill="currentColor"><circle cx="4" cy="4" r="4" /></svg>
                    {p.currentState}
                  </div>
                )}
              </div>
              {p.id === game.hostId && (
                <span className="text-[11px] font-semibold text-atlas-brass dark:text-atlas-brass-dark border border-atlas-brass dark:border-atlas-brass-dark rounded-full px-2 py-0.5 shrink-0">
                  HOST
                </span>
              )}
            </div>
          ))}

          {Array.from({ length: openSeats }).map((_, i) => (
            <div
              key={`open-${i}`}
              className="flex items-center gap-3 border border-dashed border-atlas-rule dark:border-atlas-rule-dark rounded-xl px-3.5 py-3"
            >
              <div className="w-9 h-9 rounded-full border border-dashed border-atlas-rule dark:border-atlas-rule-dark shrink-0" />
              <span className="text-sm text-atlas-ink-soft dark:text-atlas-ink-soft-dark">Waiting for a player…</span>
            </div>
          ))}
        </div>
      </main>

      {/* Controls */}
      <div className="sticky bottom-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 px-6 pt-3 pb-2 flex flex-col items-center gap-2.5">
        {isHost ? (
          <button
            onClick={startGame}
            disabled={game.players.length < 1}
            className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Start Game
          </button>
        ) : (
          <div className="text-center text-sm text-atlas-ink-soft dark:text-atlas-ink-soft-dark py-2">
            Waiting for the host to start the game…
          </div>
        )}
      </div>
    </AppScreen>
  );
}
